import React, { useState } from 'react';
import { Check, Plus, ArrowLeft, ArrowRight, Send } from 'lucide-react';
import { BackButton, Card, PrimaryButton } from '@/components/ui';
import { COLORS } from '@/data';
import { generateId, now, today } from '@/lib/utils';
import type { Creator, SubmittedClip } from '@/types';

interface CreatorViewProps {
  creators: Creator[];
  saveCreators: (creators: Creator[]) => Promise<void>;
  clips: SubmittedClip[];
  saveClips: (clips: SubmittedClip[]) => Promise<void>;
  onBack: () => void;
}

export const CreatorView: React.FC<CreatorViewProps> = ({
  creators,
  saveCreators,
  clips,
  saveClips,
  onBack
}) => {
  const [step, setStep] = useState(1);
  const [creator, setCreator] = useState<Creator | null>(null);
  const [form, setForm] = useState({
    product: 'Load Boost' as 'Load Boost' | 'Soaking Wet',
    orderId: '',
    purchaseDate: ''
  });
  const [clipForm, setClipForm] = useState({ title: '', link: '' });

  const register = async () => {
    const newCreator: Creator = {
      id: generateId('Creator'),
      ...form,
      registeredAt: now()
    };
    await saveCreators([newCreator, ...creators]);
    setCreator(newCreator);
    setStep(3);
  };

  const submitClip = async () => {
    if (!clipForm.title.trim() || !clipForm.link.trim() || !creator) return;
    const clip: SubmittedClip = {
      id: Date.now().toString(),
      clipTitle: clipForm.title,
      contentLink: clipForm.link,
      creatorId: creator.id,
      product: creator.product,
      status: 'Submitted',
      dateSubmitted: today(),
      standoutNotes: '',
    };
    await saveClips([clip, ...clips]);
    setClipForm({ title: '', link: '' });
    setStep(4);
  };

  return (
    <div className="min-h-screen bg-zinc-900 p-6">
      <div className="max-w-lg mx-auto">
        <BackButton onClick={onBack} />

        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s ? 'text-zinc-900' : 'bg-zinc-800 text-zinc-500'}`}
                style={step >= s ? { backgroundColor: COLORS.lime } : {}}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 mx-2 rounded ${step > s ? 'bg-lime-500' : 'bg-zinc-800'}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <Card title="Content Guidelines">
            <p className="text-zinc-400 text-sm mb-6">Authentic content showing real results.</p>
            <PrimaryButton onClick={() => setStep(2)}>
              I Understand <ArrowRight className="w-4 h-4" />
            </PrimaryButton>
          </Card>
        )}

        {step === 2 && (
          <Card title="About You">
            <div className="space-y-4">
              <select
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value as 'Load Boost' | 'Soaking Wet' })}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
              >
                <option value="Load Boost">Load Boost</option>
                <option value="Soaking Wet">Soaking Wet</option>
              </select>
              <input
                type="text"
                value={form.orderId}
                onChange={e => setForm({ ...form, orderId: e.target.value })}
                placeholder="Order ID *"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500 font-mono"
              />
              <input
                type="date"
                value={form.purchaseDate}
                onChange={e => setForm({ ...form, purchaseDate: e.target.value })}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setStep(1)} className="bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <PrimaryButton
                onClick={register}
                disabled={!form.orderId || !form.purchaseDate}
                className="flex-1"
              >
                Continue
              </PrimaryButton>
            </div>
          </Card>
        )}

        {step === 3 && creator && (
          <Card title="Submit Content">
            <div className="space-y-4">
              <input
                type="text"
                value={clipForm.title}
                onChange={e => setClipForm({ ...clipForm, title: e.target.value })}
                placeholder="Clip Title *"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
              />
              <input
                type="url"
                value={clipForm.link}
                onChange={e => setClipForm({ ...clipForm, link: e.target.value })}
                placeholder="Content Link *"
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white placeholder-zinc-500"
              />
            </div>
            <PrimaryButton
              onClick={submitClip}
              disabled={!clipForm.title.trim() || !clipForm.link.trim()}
              className="w-full mt-6"
            >
              <Send className="w-4 h-4" /> Submit
            </PrimaryButton>
          </Card>
        )}

        {step === 4 && (
          <Card title="Submitted!" centered>
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: COLORS.lime }}
            >
              <Check className="w-8 h-8 text-zinc-900" />
            </div>
            <div className="space-y-3">
              <PrimaryButton onClick={() => setStep(3)} className="w-full">
                <Plus className="w-4 h-4" /> Submit Another
              </PrimaryButton>
              <button onClick={onBack} className="w-full bg-zinc-700 text-zinc-300 px-5 py-3 rounded-xl">
                Done
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
